import { fromCommentDataList } from './CommentData.js';
import { fromDocumentDetailsData } from './DocumentDetailsData.js';
import { DocumentStatus } from './DocumentStatus.js';
import { fromLmcContractViolationTypeList } from './LmcContractViolationType.js';
import { FileType, RiskCategory } from './PatientDocumentData.js';
import {
  asBoolean,
  asInstant,
  mapDtoList,
  optionalEnum,
  optionalNumber,
  optionalString,
  requireNumber,
  requireObject,
  requireString,
} from './parse.js';

const TYPE_NAME = 'DocumentListData';

/**
 * Mirrors com.medprompt.document.DocumentListData, the row shape of every documents list.
 *
 * @typedef {object} DocumentListData
 * @property {string} documentId
 * @property {string|null} nhsId
 * @property {string|null} patientId
 * @property {string|null} riskCategory
 * @property {import('./DocumentDetailsData.js').DocumentDetailsData|null} documentDetails
 * @property {boolean} isMultiplePatients
 * @property {string[]} lmcContractViolations
 * @property {import('./CommentData.js').CommentData[]} comments
 * @property {string|null} assigneeId
 * @property {string|null} documentTitle
 * @property {string|null} overrideTitle
 * @property {string|null} senderMeshMailboxId
 * @property {string|null} senderEmail
 * @property {number|null} pageCount
 * @property {string|null} fileType
 * @property {number} timestamp
 * @property {number|string|null} filedAwayAt
 * @property {string|null} status
 */

/**
 * @param {object} raw
 * @returns {DocumentListData}
 */
export function fromDocumentListData(raw) {
  const source = requireObject(raw, TYPE_NAME);
  return {
    documentId: requireString(source, 'documentId', TYPE_NAME),
    nhsId: optionalString(source.nhsId),
    patientId: optionalString(source.patientId),
    riskCategory: optionalEnum(source.riskCategory, RiskCategory, TYPE_NAME, 'riskCategory'),
    documentDetails: source.documentDetails == null ? null : fromDocumentDetailsData(source.documentDetails),
    isMultiplePatients: asBoolean(source.isMultiplePatients),
    lmcContractViolations: fromLmcContractViolationTypeList(source.lmcContractViolations, TYPE_NAME),
    comments: fromCommentDataList(source.comments),
    assigneeId: optionalString(source.assigneeId),
    documentTitle: optionalString(source.documentTitle),
    overrideTitle: optionalString(source.overrideTitle),
    senderMeshMailboxId: optionalString(source.senderMeshMailboxId),
    senderEmail: optionalString(source.senderEmail),
    pageCount: optionalNumber(source.pageCount),
    fileType: optionalEnum(source.fileType, FileType, TYPE_NAME, 'fileType'),
    timestamp: requireNumber(source, 'timestamp', TYPE_NAME),
    filedAwayAt: asInstant(source.filedAwayAt),
    status: optionalEnum(source.status, DocumentStatus, TYPE_NAME, 'status'),
  };
}

/**
 * @param {unknown} raw
 * @returns {DocumentListData[]}
 */
export function fromDocumentListDataList(raw) {
  return mapDtoList(raw, TYPE_NAME, fromDocumentListData);
}
